/*
 * CustomerMap — Leaflet map of active-customer pins.
 *
 * Uses OpenStreetMap tiles (no key) and react-leaflet-cluster to group
 * pins at zoom-out. The parent supplies pins already filtered to
 * active accounts with resolved lat/lng.
 */
import React, { useMemo } from "react";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import MarkerClusterGroup from "react-leaflet-cluster";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { useNavigate } from "react-router-dom";
import { fmtMoney } from "@/lib/api";

// Fix Leaflet's default icon path (webpack strips the relative asset paths).
// We swap in the CDN URLs so pins actually render.
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

const HEALTH_TAG = {
  green: "bg-emerald-100 text-emerald-800",
  amber: "bg-amber-100 text-amber-800",
  red: "bg-red-100 text-red-800",
};

export default function CustomerMap({ pins = [], height = 460 }) {
  const nav = useNavigate();

  const center = useMemo(() => {
    if (!pins.length) return [54.5, -2.5]; // UK-ish default
    const lat = pins.reduce((a, p) => a + p.latitude, 0) / pins.length;
    const lng = pins.reduce((a, p) => a + p.longitude, 0) / pins.length;
    return [lat, lng];
  }, [pins]);

  return (
    <div
      data-testid="customer-map"
      className="rounded-md overflow-hidden border border-slate-200"
      style={{ height }}
    >
      <MapContainer
        center={center}
        zoom={pins.length > 1 ? 6 : 10}
        scrollWheelZoom={false}
        style={{ height: "100%", width: "100%" }}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <MarkerClusterGroup chunkedLoading>
          {pins.map((p) => (
            <Marker key={p.id} position={[p.latitude, p.longitude]}>
              <Popup>
                <div className="min-w-[190px]" data-testid={`map-popup-${p.id}`}>
                  <div className="font-semibold text-slate-900">{p.name}</div>
                  <div className="text-xs text-slate-600 mt-1">
                    <span className="uppercase tracking-wider text-slate-400 mr-1">Owner:</span>
                    {p.owner_name || "— Unassigned —"}
                  </div>
                  <div className="text-xs text-slate-600">
                    <span className="uppercase tracking-wider text-slate-400 mr-1">MRR:</span>
                    {fmtMoney(p.mrr || 0)}
                  </div>
                  <div className="flex items-center gap-1 mt-2">
                    <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider bg-emerald-100 text-emerald-800">
                      Active
                    </span>
                    {p.health && (
                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider ${HEALTH_TAG[p.health] || "bg-slate-100 text-slate-700"}`}>
                        {p.health}
                      </span>
                    )}
                    {p.postcode && (
                      <span className="ml-auto text-[10px] font-mono text-slate-500">{p.postcode}</span>
                    )}
                  </div>
                  <button
                    className="mt-3 w-full text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded px-2 py-1.5"
                    onClick={() => nav(`/app/accounts/${p.id}`)}
                    data-testid={`map-open-${p.id}`}
                  >
                    Open account →
                  </button>
                </div>
              </Popup>
            </Marker>
          ))}
        </MarkerClusterGroup>
      </MapContainer>
    </div>
  );
}
